import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as FeedbackIcon } from "../../assets/icons/svg/feedback_icon.svg";
import { EnhancedTokenManager } from "../../utils/tokenManager";
import { API_URLS, BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface DeveloperFeedbackCardProps {
  appName: string;
  feedbackCount: number;
  daysAgo: string;
  imageUrl: string;
  onClick?: () => void;
  isNew?: boolean;
  isReshared?: boolean;
  sharedBy?: string;
  cardId?: number;
  preloadedDevelopers?: Developer[];
  workspaceOwnerName?: string;
  workspaceOwnerProfile?: string | null;
}

// Developer interface
interface Developer {
  email: string;
  name: string | null;
  profile_image: string | null;
}

// Combined User interface (matching DeveloperAvatarsGroup)
interface User {
  email: string;
  name: string | null;
  profile_image: string | null;
  role: string;
  isOwner: boolean;
}

// Helper to get full image URL
const getFullImageUrl = (imagePath: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${BASE_URL}/${cleanPath}`;
};

// Helper to get initials
const getInitials = (name: string | null, email: string): string => {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
};

const DeveloperFeedbackCard: React.FC<DeveloperFeedbackCardProps> = ({
  appName,
  feedbackCount,
  daysAgo,
  imageUrl,
  onClick,
  isNew = false,
  isReshared = false,
  cardId,
  preloadedDevelopers,
  workspaceOwnerName, // ✅ NEW
  workspaceOwnerProfile, // ✅ NEW
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [users, setUsers] = useState<User[]>([]); // ✅ CHANGED: Use User[] instead of Developer[]
  const [isLoadingDevs, setIsLoadingDevs] = useState(!preloadedDevelopers);
  const imgRef = useRef<HTMLImageElement>(null);

  // Lazy load images with Intersection Observer
  useEffect(() => {
    if (!imgRef.current || imageError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && imgRef.current) {
            imgRef.current.src = imageUrl;
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "50px",
      },
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [imageUrl, imageError]);

  // ✅ UPDATED: Combine owner and developers like DeveloperAvatarsGroup
  useEffect(() => {
    console.log(
      `🔍 DeveloperFeedbackCard ${cardId} - Checking pre-loaded data:`,
      {
        hasPreloadedDevelopers: !!preloadedDevelopers,
        preloadedCount: preloadedDevelopers?.length || 0,
        hasOwner: !!workspaceOwnerName,
        ownerName: workspaceOwnerName,
        appName: appName,
        isReshared: isReshared,
      },
    );

    const allUsers: User[] = [];

    // Add workspace owner first (if available)
    if (workspaceOwnerName) {
      allUsers.push({
        email: "",
        name: workspaceOwnerName,
        profile_image: workspaceOwnerProfile ?? null, // ✅ Convert undefined to null
        role: "Owner",
        isOwner: true,
      });
    }

    // Add developers
    if (preloadedDevelopers && preloadedDevelopers.length > 0) {
      preloadedDevelopers.forEach((dev) => {
        allUsers.push({
          email: dev.email,
          name: dev.name,
          profile_image: dev.profile_image,
          role: "Developer",
          isOwner: false,
        });
      });
    }

    setUsers(allUsers);
    setIsLoadingDevs(false);

    console.log(
      `✅ Using ${allUsers.length} user(s) for card ${cardId} (${appName}):`,
      allUsers.map((u) => ({ name: u.name, email: u.email, role: u.role })),
    );
  }, [
    cardId,
    preloadedDevelopers,
    workspaceOwnerName,
    workspaceOwnerProfile,
    appName,
    isReshared,
  ]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    console.error(`Failed to load developer feedback card image: ${imageUrl}`);
    setImageError(true);
    setImageLoaded(true);
  };

  const PlaceholderContent = () => (
    <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <svg
          className="w-8 h-8 text-blue-400 mx-auto mb-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
        <p className="text-xs text-blue-500">Shared feedback</p>
      </div>
    </div>
  );

  const ImageShimmer = () => (
    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 opacity-50" />
    </div>
  );

  const collapsedOffset = -16;

  return (
  <div
    className="rounded-xl border border-blue-200 overflow-hidden max-w-sm bg-white transition-all cursor-pointer hover:shadow-lg relative flex flex-col"
    onClick={onClick}
    style={{ 
      touchAction: 'pan-y pan-x', // ✅ Allow scroll gestures
      userSelect: 'none' // ✅ Prevent text selection during scroll
    }}
  >
      {/* Image Section */}
      <div className="h-44 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 relative">
        {!imageLoaded && !imageError && <ImageShimmer />}

        {!imageError && imageUrl ? (
          <img
            ref={imgRef}
            alt={`${appName} preview`}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        ) : (
          <PlaceholderContent />
        )}
      </div>

      {/* Content Section */}
      <div className="p-4 flex flex-col flex-1">
        {/* App Name with Developer Avatars */}
        <div className="flex flex-col gap-2 mb-2">
          {/* App Name */}
          <div
            className="text-lg font-semibold text-gray-900 truncate"
            title={appName}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {appName}
          </div>

          {/* ✅ UPDATED: User Avatars (Owner + Developers) */}
          {!isLoadingDevs && users.length > 0 && (
            <div
              className="flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Count Badge */}
              <div className="relative w-6 h-6">
                <div className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-[#1E1F26] text-white text-[10px] font-semibold border-[2px] border-white">
                  {users.length}
                </div>
              </div>

              {/* User Avatars (Owner + Developers) */}
              {users.map((user, index) => {
                const initials = getInitials(user.name, user.email);
                const fullImageUrl = getFullImageUrl(user.profile_image);

                return (
                  <div
                    key={user.isOwner ? "owner" : user.email}
                    className="relative"
                    style={{
                      marginLeft: `${collapsedOffset}px`,
                      zIndex: users.length - index,
                    }}
                  >
                    <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center border-[2px] border-white">
                      {fullImageUrl ? (
                        <img
                          src={fullImageUrl}
                          alt={user.name || user.email}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-full h-full ${
                            user.isOwner
                              ? "bg-gradient-to-br from-blue-400 to-indigo-500"
                              : "bg-gradient-to-br from-purple-400 to-pink-400"
                          } flex items-center justify-center text-white font-semibold text-xs`}
                        >
                          {initials}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Badge Container - Fixed Height */}
        <div className="h-7 mb-1">
          {isNew && !isReshared && (
            <span
              className="px-[10px] py-1 bg-[#28B44624] text-[#2DA146] text-xs 4xl:text-[14px]  rounded-full inline-block"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Just shared
            </span>
          )}
          {isReshared && (
            <span
              className="px-[10px] py-1 bg-[#3B82F624] text-[#3B82F6] text-xs 4xl:text-[14px] rounded-full inline-block"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Clarified feedback
            </span>
          )}
        </div>

        {/* Bottom Section */}
        <div className="mt-0">
          <div className="flex justify-between text-sm mb-3">
            <div className="flex items-center text-black gap-1">
              <FeedbackIcon />
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                {feedbackCount} feedback{feedbackCount !== 1 ? "s" : ""}
              </span>
            </div>
            <div
              className="text-gray-500"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {daysAgo}
            </div>
          </div>

          <div className="border-t border-blue-100" />

          <div className="pt-3">
            <div
              className="text-sm font-medium text-blue-600"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              View Feedback
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperFeedbackCard;
